import React, { useState, useEffect } from "react";
import { Modal, FormActions, Input, Select, IconsLarge } from "../../shared";
import type { SelectOption } from "../../shared";
import { subcategoriesApi } from "../../../services";
import type { CategoryAttributes } from "../../../types";
import type { SubcategoryAttributes } from "../../../types";

interface CreateSubcategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  companyId: number;
  categories: CategoryAttributes[];
  editingSubcategory?: SubcategoryAttributes | null;
  /** When set, category select is hidden and this id is used for create */
  fixedCategoryId?: number;
}

export const CreateSubcategoryModal: React.FC<CreateSubcategoryModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  companyId,
  categories,
  editingSubcategory,
  fixedCategoryId,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [categoryId, setCategoryId] = useState<number | "">("");

  const categoryOptions: SelectOption[] = categories.map((c) => ({
    value: c.id,
    label: c.name,
  }));

  useEffect(() => {
    if (editingSubcategory) {
      setName(editingSubcategory.name);
      setCategoryId(editingSubcategory.categoryId);
    } else {
      setName("");
      setCategoryId(fixedCategoryId ?? (categories.length > 0 ? categories[0].id : ""));
    }
    setError(null);
  }, [editingSubcategory, isOpen, categories, fixedCategoryId]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError("Nome da categoria é obrigatório");
      return;
    }
    const cid = fixedCategoryId ?? (typeof categoryId === "number" ? categoryId : Number(categoryId));
    if (!cid) {
      setError("Selecione uma categoria mãe");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (editingSubcategory) {
        const response = await subcategoriesApi.update(editingSubcategory.id, {
          name: name.trim(),
        });
        if (response.success) {
          onSuccess();
          onClose();
        } else {
          setError(response.error ?? "Falha ao atualizar categoria");
        }
      } else {
        const response = await subcategoriesApi.create({
          name: name.trim(),
          categoryId: cid,
          companyId,
        });
        if (response.success) {
          onSuccess();
          onClose();
        } else {
          setError(response.error ?? "Falha ao criar categoria");
        }
      }
    } catch {
      setError(
        editingSubcategory
          ? "Falha ao atualizar categoria"
          : "Falha ao criar categoria"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setName("");
    setCategoryId("");
    setError(null);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={editingSubcategory ? "Editar categoria" : "Nova categoria"}
      icon={<IconsLarge.Folder />}
    >
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3 mb-6">
          <span className="text-xl">⚠️</span>
          <span className="text-red-800 font-medium">{error}</span>
        </div>
      )}

      <div className="space-y-6">
        <Input
          label="Nome da categoria"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSubmit();
            if (e.key === "Escape") handleClose();
          }}
          placeholder="Ex: Alimentação, Combustível..."
          disabled={loading}
          required
        />

        {fixedCategoryId == null && (
          <Select
            label="Categoria mãe"
            value={categoryId}
            onChange={(v) => setCategoryId(Number(v))}
            options={categoryOptions}
            placeholder="Selecione a categoria mãe"
            disabled={loading || !!editingSubcategory}
            required
            searchable={categoryOptions.length > 6}
          />
        )}

        <FormActions
          onCancel={handleClose}
          onSubmit={handleSubmit}
          loading={loading}
          isEditing={!!editingSubcategory}
          submitType="button"
          submitText={
            editingSubcategory ? "Atualizar categoria" : "Criar categoria"
          }
        />
      </div>
    </Modal>
  );
};
